/**
 * Type-safe route definitions for the Meetball app.
 *
 * Routes that navigate to detail pages require a `returnTo` parameter
 * to enable predictable back button behavior. TypeScript enforces this
 * at compile time, preventing navigation without context.
 *
 * @see docs/navigation-system.md for usage guide
 */

import {
  getCommunityPath,
  getEventPath,
  getQuestionPath,
  getProfilePath,
  getEventEditPath,
  getQuestionEditPath,
  getProfileEditPath,
  getChatPath,
  getPostChatPath,
} from '@utils/slugHelpers';
import { logger } from '@utils/logger';
import { validateReturnTo } from '@utils/returnToValidation';
import { featureFlags } from '@config/featureFlags';

const HOME_PATH = '/home';
const EVENTS_PATH = '/events';

interface ReturnToOptions {
  returnTo: string;
  /** Additional query parameters appended after returnTo (e.g., { tab: 'members' }) */
  query?: Record<string, string>;
}

/**
 * Safely encode the returnTo parameter for URL inclusion.
 * Validates that it's a relative path to prevent open redirect vulnerabilities.
 * Appends any extra query parameters from opts.query.
 */
function buildQuery(returnTo: string, query?: Record<string, string>): string {
  const params = new URLSearchParams();

  if (validateReturnTo(returnTo)) {
    params.set('returnTo', returnTo);
  } else {
    logger.warn(
      `[routes] Invalid returnTo value: "${returnTo}". Must be a safe relative in-app path`,
    );
  }

  if (query) {
    for (const [key, value] of Object.entries(query)) {
      params.set(key, value);
    }
  }

  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/** Entity with id and optional slug for path generation. At least one of id/slug must be non-null. */
interface SlugEntity {
  id: string | null;
  slug?: string | null;
}

/**
 * Route pattern of a chat about someone else's post, which `routes.chatAboutPost` builds and the
 * Chat screen reads (`questionId`). Registered in src/router.tsx beside `/chat/:slugOrId`.
 */
export const CHAT_ABOUT_POST_PATH = '/chat/post/:questionId';

export const routes = {
  // Root pages (no returnTo needed)
  home: () => HOME_PATH,
  events: () => EVENTS_PATH,
  /**
   * Where a user goes when they have no specific destination: the root (`/`)
   * redirect and the post-signup default both resolve here. Gated by
   * `featureFlags.landOnEvents` so the landing can be reverted by flipping one
   * boolean rather than editing call sites.
   */
  landing: () => (featureFlags.landOnEvents ? EVENTS_PATH : HOME_PATH),
  messages: () => '/messages',
  notifications: () => '/notifications',
  settings: () => '/settings',

  // Admin routes
  admin: {
    // The Settings screen lives at /settings and switches sections via query
    // param — there is no /settings/admin path route (it fell through to the
    // 404 catch-all, breaking every admin back button).
    settings: () => '/settings?section=admin',
    analytics: () => '/settings/admin/analytics',
    meetverse: () => '/settings/admin/meetverse',
    notificationTemplates: () => '/settings/admin/notification-templates',
    notificationTemplateNew: () => '/settings/admin/notification-templates/new',
    notificationTemplateEdit: (id: string) => `/settings/admin/notification-templates/${id}/edit`,
    notificationSettings: () => '/settings/admin/notification-settings',
    icpSearch: () => '/settings/admin/icp-search',
    reports: () => '/settings/admin/reports',
  },

  // Event routes - accepts entity with slug for SEO-friendly URLs
  event: {
    /** Navigate to event details with returnTo context */
    details: (entity: SlugEntity, opts: ReturnToOptions) =>
      `${getEventPath(entity)}${buildQuery(opts.returnTo, opts.query)}`,
    /** Navigate to event edit with returnTo context */
    edit: (entity: SlugEntity, opts: ReturnToOptions) =>
      `${getEventEditPath(entity)}${buildQuery(opts.returnTo, opts.query)}`,
    create: () => '/create-event',
  },

  // Question routes - accepts entity with slug for SEO-friendly URLs
  question: {
    /** Navigate to question details with returnTo context */
    details: (entity: SlugEntity, opts: ReturnToOptions) =>
      `${getQuestionPath(entity)}${buildQuery(opts.returnTo, opts.query)}`,
    /** Navigate to question edit with returnTo context */
    edit: (entity: SlugEntity, opts: ReturnToOptions) =>
      `${getQuestionEditPath(entity)}${buildQuery(opts.returnTo, opts.query)}`,
    create: () => '/create-question',
  },

  /**
   * The Poll composer (TECH-197 FE-2b), started from a Space or an Event, which it opens with that
   * place ticked. `returnTo` lands the poster back where they started.
   */
  createPoll: (place: { spaceId: string } | { eventId: string }, opts?: { returnTo?: string }) => {
    const params = new URLSearchParams();
    if ('spaceId' in place) params.set('space', place.spaceId);
    else params.set('event', place.eventId);
    if (opts?.returnTo && validateReturnTo(opts.returnTo)) params.set('returnTo', opts.returnTo);
    return `/create-poll?${params.toString()}`;
  },

  /** Your live polls (TECH-867 H5), the author's list. */
  livePolls: () => '/live',

  /** A live poll's host view (TECH-867 H1), author only: `/live/{live_code}/host`. */
  liveHost: (liveCode: string) => `/live/${encodeURIComponent(liveCode)}/host`,

  /** The edit composer of a poll (TECH-197 E4): `/create-poll?edit={questionId}`. */
  editPoll: (questionId: string, opts?: { returnTo?: string }) => {
    const params = new URLSearchParams();
    params.set('edit', questionId);
    if (opts?.returnTo && validateReturnTo(opts.returnTo)) params.set('returnTo', opts.returnTo);
    return `/create-poll?${params.toString()}`;
  },

  // Profile routes - accepts entity with slug for SEO-friendly URLs
  profile: {
    /** Navigate to profile view with returnTo context */
    view: (entity: SlugEntity, opts: ReturnToOptions) =>
      `${getProfilePath(entity)}${buildQuery(opts.returnTo, opts.query)}`,
    /** Navigate to profile edit with returnTo context */
    edit: (entity: SlugEntity, opts: ReturnToOptions) =>
      `${getProfileEditPath(entity)}${buildQuery(opts.returnTo, opts.query)}`,
  },

  // Community routes - accepts entity with slug for SEO-friendly URLs
  community: {
    /** Navigate to community details with returnTo context */
    details: (entity: SlugEntity, opts: ReturnToOptions) =>
      `${getCommunityPath(entity)}${buildQuery(opts.returnTo, opts.query)}`,
    create: () => '/create-community',
    /** Navigate to the shareable space welcome/intro landing screen */
    welcome: (entity: SlugEntity) => `${getCommunityPath(entity)}/welcome`,
    /** Navigate to post-creation setup wizard */
    setup: (entity: SlugEntity) => `${getCommunityPath(entity)}/setup`,
    /** Navigate to the space topic management screen */
    manageTopics: (entity: SlugEntity) => `${getCommunityPath(entity)}/manage-topics`,
    /** Navigate to Manage Partnerships, the Partners page's ordering screen (TECH-86) */
    managePartnerships: (entity: SlugEntity) => `${getCommunityPath(entity)}/manage-partnerships`,
    /** Navigate to admin invite screen */
    invite: (entity: SlugEntity, opts: ReturnToOptions) =>
      `${getCommunityPath(entity)}/invite${buildQuery(opts.returnTo, opts.query)}`,
    /** Navigate to space analytics dashboard with returnTo context */
    statistics: (entity: SlugEntity, opts: ReturnToOptions) =>
      `${getCommunityPath(entity)}/statistics${buildQuery(opts.returnTo, opts.query)}`,
  },

  // Chat routes - accepts entity with slug for SEO-friendly URLs
  chat: (entity: SlugEntity, opts: ReturnToOptions) =>
    `${getChatPath(entity)}${buildQuery(opts.returnTo, opts.query)}`,
  /** A chat with the author of someone else's post, addressed by the post (TECH-693) */
  chatAboutPost: (question: { id: string }, opts: ReturnToOptions) =>
    `${getPostChatPath(question.id)}${buildQuery(opts.returnTo, opts.query)}`,
} as const;
